/**
 * Swamithra — Expert accounts.
 *
 * Experts are PROVISIONED, never self-registered: there is no sign-up route in
 * the panel and a POST without a super_admin session is a 403 (US-01-005.13).
 * The role is never sent by a client — it is set by the provisioning task for
 * the one super_admin and defaulted to `expert` for everyone else
 * (US-01-005.22), which is why `createExpertInputSchema` has no role field and
 * is `.strict()`.
 */

import { z } from "zod"

import { expertRoleSchema } from "./enums"
import { e164Schema, mobileNumberSchema } from "./phone"
import { fullNameSchema, optionalEmailSchema } from "./user"

export const expertSchema = z.object({
	id: z.string(),
	fullName: z.string(),
	mobileNumber: e164Schema,
	email: z.string().nullable(),
	role: expertRoleSchema,
	isActive: z.boolean(),
	createdAt: z.string(),
	/** Null until the first successful OTP verification — rendered 'Never'. */
	lastLoginAt: z.string().nullable(),
})

export type Expert = z.infer<typeof expertSchema>

/** `lastLoginAt` as the Experts list renders it (US-01-005.16). */
export function lastLoginLabel(expert: Pick<Expert, "lastLoginAt">): string | null {
	return expert.lastLoginAt === null ? "Never" : null
}

export const createExpertInputSchema = z
	.object({
		fullName: fullNameSchema,
		mobileNumber: mobileNumberSchema,
		email: optionalEmailSchema.optional(),
	})
	.strict()

export type CreateExpertInput = z.infer<typeof createExpertInputSchema>

/** Activate or deactivate. The only mutation the Experts list offers. */
export const expertActivationSchema = z.object({ isActive: z.boolean() }).strict()

/**
 * Who is signed in to the panel, as `GET /expert/me` answers.
 *
 * A SUBSET OF THE EXPERT RECORD, and that is the service's choice: the probe
 * exists so the header can render a name and the navigation can hide
 * expert-management from a plain `expert` (US-01-005.5), so it sends an id, a
 * name, a number and a role and nothing else. Requiring the full record here —
 * `isActive`, `createdAt`, `lastLoginAt` — failed the parse and took the whole
 * panel to the login screen with a perfectly good cookie in the jar.
 *
 * `isActive` is not on it and does not need to be: it is re-checked server-side
 * on EVERY authenticated request, which is what makes a deactivation take effect
 * on the Expert's very next call rather than at their next sign-in
 * (US-01-005.17).
 *
 * The JWT is carried ONLY in an httpOnly, Secure, SameSite=Lax cookie — the
 * panel is web-only, so unlike the user app there is no token in the body at all
 * (US-01-005.12).
 */
export const panelIdentitySchema = z.object({
	id: z.string(),
	fullName: z.string(),
	mobileNumber: e164Schema,
	role: expertRoleSchema,
})

export type PanelIdentity = z.infer<typeof panelIdentitySchema>

/**
 * What the panel login screen is told once a code has been dispatched.
 *
 * A DEACTIVATED Expert and an UNKNOWN number must be indistinguishable: both get
 * the same generic `not_authorised` failure and no message of any kind is sent
 * (US-01-005.6/.7). So this shape is only ever reached by an active Expert, and
 * it carries nothing about the account beyond the timing the screen needs.
 */
export const panelOtpChallengeSchema = z.object({
	expiresAt: z.string(),
	resendAvailableAt: z.string(),
})

export type PanelOtpChallenge = z.infer<typeof panelOtpChallengeSchema>
